import { Loader } from "@googlemaps/js-api-loader";

/**
 * Google rejects a bad key through a global callback and never fails the pending request itself —
 * the geocoder callback just never fires — so the geocoder subscribes here.
 */
export interface IGoogleMapsAuth {
    /** Calls the listener once the key is rejected (straight away if it already was); returns an unsubscribe. */
    onAuthFailure(listener: () => void): () => void;

    isKeyRejected(): boolean;
}

export class GoogleMapsLoader {
    private static _loader: Loader | null = null;
    private static _isListening = false;
    private static _isAuthFailed = false;
    private static readonly _authFailureListeners = new Set<() => void>();

    static getLoader(apiKey: string): Loader {
        // The Maps script loads once per page with the first key it sees, so a keyless loader
        // would lock out every later caller that does have one.
        if (!apiKey)
            throw new Error("A Google Maps API key is required to load the Maps script.");

        if (!this._loader) {
            // Google only reports a rejected key during the script's own start-up, so the
            // listener has to be in place before the script loads or the failure goes unheard.
            this._listenForAuthFailure();

            this._loader = new Loader({
                apiKey,
                version: "quarterly" // same intent as your script tag
                // do NOT pass libraries here if you plan to use importLibrary()
            });
        }

        return this._loader;
    }

    static onAuthFailure(listener: () => void): () => void {
        this._listenForAuthFailure();

        if (this._isAuthFailed) {
            listener();
            return () => undefined;
        }

        this._authFailureListeners.add(listener);
        return () => this._authFailureListeners.delete(listener);
    }

    static isKeyRejected(): boolean {
        return this._isAuthFailed;
    }

    private static _listenForAuthFailure(): void {
        if (this._isListening)
            return;

        this._isListening = true;
        const previousHandler = window.gm_authFailure;

        window.gm_authFailure = () => {
            this._isAuthFailed = true;
            this._authFailureListeners.forEach(listener => listener());
            this._authFailureListeners.clear();

            // Ours run first so a page handler that throws cannot leave callers waiting.
            previousHandler?.();
        };
    }
}
